import { useState, useEffect } from 'react';
import {
  Dialog, IconButton,
  InputAdornment,
  List as MuiList, ListItem, ListItemButton,
  ListItemIcon, ListItemText, TextField,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import { useApi, useCurrentUser, useCurrentTeamId } from 'lib/contexts/ApplicationState';
import { ContentList, ContentListsItemType } from 'lib/types';
import Link from 'next/link';
import { QuickAddProject } from 'components/AddEditProject/AddEditProject';

interface Props {
  itemId: number;
  /** The team id to use if not the current team, useful to override the app state teamId
   * for objects like jobs that have a team associated already */
  itemType: ContentListsItemType;
  onClose: () => void;
  context: 'project' | 'list';
  excludePersonal?: boolean;
}

export const SaveToProjectListModal = ({
  itemId, itemType, onClose, context, excludePersonal = false,
}: Props) => {
  const currentUser = useCurrentUser();
  const API = useApi();
  const currentTeamId = useCurrentTeamId();
  const { enqueueSnackbar } = useSnackbar();

  const [isSendingData, setIsSendingData] = useState(false);
  const [lists, setLists] = useState<ContentList[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fetchLists = async () => {
      const result = await API.getProjectLists(itemId, itemType);
      if (excludePersonal) {
        // Filter out personal lists if excludePersonal is true
        // TODO: Go by team id, in general the team id of a job should match the project team id
        setLists(result.filter((l) => !!l.teamName).sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        setLists(result.sort((a, b) => a.name.localeCompare(b.name)));
      }
    };
    fetchLists();
  }, [currentUser]);

  const addToList = async (list: ContentList) => {
    try {
      setIsSendingData(true);
      await API.addItemToList(list.id, itemId, itemType);
      setIsSendingData(false);
      onClose();
      enqueueSnackbar(`Added to ${list.name}!`, {
        variant: 'success',
        autoHideDuration: 9000,
        action: (
          <Link href={`/projects/${list.publicProjectId}`}>
            View project
          </Link>
        ),
      });
    } catch (error) {
      enqueueSnackbar(`Error adding ${itemType} to list: ${error}`, {
        variant: 'error',
      });
    }
  };

  const removeFromList = async (list: ContentList) => {
    try {
      setIsSendingData(true);
      await API.removeItemFromList(list.id, itemId);
      setIsSendingData(false);
      onClose();
      enqueueSnackbar(`Removed from ${list.name}`, {
        variant: 'success',
        action: (
          <Link href={`/projects/${list.publicProjectId}`}>
            View project
          </Link>
        ),
      });
    } catch (err) {
      setIsSendingData(false);
      enqueueSnackbar(`Error removing ${itemType} from list: ${err}`, {
        variant: 'error',
      });
    }
  };

  const save = async (list: ContentList) => {
    if (!list.id) return;
    if (list.contentListItemId) {
      removeFromList(list);
      return;
    }
    addToList(list);
  };

  const filterItemsBySearch = (items: any[], searchText: string) => {
    if (!searchText) return items;
    const query = searchText.toLowerCase();
    return items.filter((item) => {
      if (item.name) return item.name.toLowerCase().includes(query);
      if (item.teamName) return item.teamName.toLowerCase().includes(query);
      return false;
    });
  };

  const [searchQuery, setSearchQuery] = useState('');
  const filteredListsBySearch = filterItemsBySearch(
    lists || [], searchQuery,
  );

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <div className="absolute right-2 top-2 text-orange">
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <div className="p-4">
        <>
          {open ? (
            <QuickAddProject
              onCancel={() => setOpen(false)}
              onSave={onClose}
              project={{}}
            />
          )
            : (
              <>
                <div className="flex flex-wrap sm:flex-nowrap items-center">
                  <h1 className="text-lg text-orange font-bold mr-5">
                    Save
                    {' '}
                    {itemType}
                    {' '}
                    to a
                    {' '}
                    {context}
                  </h1>
                </div>
                <div>
                  <div className="p-2 sticky top-0 bg-white dark:bg-appbg-dark z-10">
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search projects"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      InputProps={{
                        endAdornment: searchQuery && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSearchQuery('');
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                  <MuiList>
                    {currentTeamId && !lists.length && (
                      <div className="text-center p-2">
                        <p className="mb-2">
                          No projects found.
                        </p>
                        <Link className="btn-primary" href="/projects/new">
                          Create a project
                        </Link>
                      </div>
                    )}
                    {filteredListsBySearch.map((l) => (
                      <ListItem key={l.id} disablePadding>
                        <ListItemButton
                          disabled={isSendingData}
                          onClick={() => save(l)}
                        >
                          <ListItemIcon>
                            {(l.contentListItemId) ? <CheckIcon color="success" /> : <AddIcon color="inherit" />}
                          </ListItemIcon>
                          <ListItemText>
                            <div>
                              {l.name}
                              {l.teamName && (
                                <span className="font-light text-xs ml-2">
                                  (
                                  {l.teamName}
                                  )
                                </span>
                              )}
                            </div>
                          </ListItemText>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </MuiList>
                </div>
                <div>
                  <button type="button" className="py-4 w-full hover:bg-hintgray hover:dark:bg-darkgray" onClick={() => setOpen(true)}>
                    <AddIcon />
                    {' '}
                    Create a New Project
                  </button>
                </div>
              </>
            )}
        </>
      </div>
    </Dialog>
  );
};
